import { makeAutoObservable, reaction } from 'mobx'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { GenderProfileData } from 'store/profile/GenderProfileData'
import { ProfileCommonFormStore } from 'store/profile/ProfileCommonFormStore'
import { BaseIdentityRequest, BodyIndex, BodyThickness, BustIndex, DimensionsJson, ExtendedShapeJson, Gender, getShouldersConfig, Identity, IdentityMode, ShapeType, SkinIndex } from 'type/Identity'
import { GenderConfig, MinMax } from 'type/IdentityConfig'
import { INCH } from 'type/LengthUnit'
import { Photo } from 'type/Options'
import { middle, prefer } from 'util/array'
import { getMid, inRange, parseRange } from 'util/range'

function selectMyHeightRange(cfg: GenderConfig, g: string): string | undefined {
  const { gender, height } = me.metrics
  if (gender !== g || !height) return undefined
  return cfg.heights_config.find(it => {
    const range = parseRange(it.height)
    return inRange(range, height)
  })?.height
}

function selectHeightRange(cfg: GenderConfig): string {
  return middle(cfg.heights_config, true)?.height ?? '172-178'
}

function getHeightFromRange(heightRange: string): number {
  const range = parseRange(heightRange)
  return getMid(range) ?? 172
}

function selectLeg(legs: number[]): number {
  return middle(legs, true) ?? 32
}

function selectShapeType(cfg: GenderConfig): ShapeType {
  return prefer(cfg.shape_types, ShapeType.HOURGLASS)
}

function selectBodyThickness(shape: ShapeType, cfg: GenderConfig): BodyThickness {
  const thicknesses = cfg.shape_types_config[shape] ?? cfg.body_thickness
  return prefer(thicknesses, BodyThickness.SLIM)
}

function fixMinMax(value: number, cfg: MinMax, inch?: boolean) {
  const min = inch ? Math.round(cfg.min / INCH) : cfg.min
  const max = inch ? Math.round(cfg.max / INCH) : cfg.max
  const val = inch ? Math.round(value / INCH) : value

  if (val <= min) return cfg.min
  if (val >= max) return cfg.max
  return Math.round(value)
}

function fixMode(mode: IdentityMode | undefined, photo: Photo): IdentityMode | undefined {
  if (photo === 'selfie' && mode === IdentityMode.SIMPLE) return undefined
  return mode
}

function getDefaultIdentityMode(photo: Photo): IdentityMode {
  if (photo === 'selfie') return opt.body[0] ?? IdentityMode.BASE
  if (photo === 'simple') return IdentityMode.SIMPLE
  if (photo === 'full') return IdentityMode.DETAILED
  return IdentityMode.BASE
}

export class ProfileGenderFormStore {
  readonly gender: Gender
  readonly cfg: GenderConfig
  readonly common: ProfileCommonFormStore
  data!: GenderProfileData

  skin!: SkinIndex
  mode!: IdentityMode
  body!: BodyIndex
  bust!: BustIndex
  shape!: ShapeType
  thickness!: BodyThickness
  heightRange!: string
  legExtended!: number
  height!: number
  shoulders!: number
  chest!: number
  waist!: number
  hips!: number
  legDetailed!: number

  constructor(gender: Gender, cfg: GenderConfig, identity: Identity | undefined,
    common: ProfileCommonFormStore, photo: Photo) {
    makeAutoObservable(this)
    this.gender = gender
    this.cfg = cfg
    this.common = common
    this.init(identity, photo)
    this.fix()
    this.save()
    reaction(() => this.common.unit, () => this.fix())
  }

  get male() {
    return this.gender === 'male'
  }

  reset() {
    this.apply()
    this.fix()
  }

  beforeSave() {
    this.save()
  }

  get leg(): number {
    return this.mode === IdentityMode.EXTENDED ? this.legExtended : this.legDetailed
  }

  get canSave(): boolean {
    return this.common.canSave
  }

  setSkin(value: SkinIndex) {
    this.skin = value
  }

  setMode(value: IdentityMode) {
    this.mode = value
  }

  setBody(value: BodyIndex) {
    this.body = value
  }

  setBust(value: BustIndex) {
    this.bust = value
  }

  setShape(value: ShapeType) {
    this.shape = value
    this.fix()
  }

  setThickness(value: BodyThickness) {
    this.thickness = value
  }

  setHeightRange(value: string) {
    this.heightRange = value
    this.fixLegExtended()
  }

  setLeg(value: number) {
    if (this.mode === IdentityMode.EXTENDED) {
      this.legExtended = value
      this.fixLegExtended()
    }
    if (this.mode === IdentityMode.DETAILED) {
      this.legDetailed = value
      this.fixLegDetailed()
    }
  }

  setShoulders(value: number) {
    this.shoulders = this.fixShoulders(value)
  }

  setHeight(value: number) {
    this.height = this.fixUnit(value, this.cfg.dimensions.height)
  }

  setChest(value: number) {
    this.chest = this.fixUnit(value, this.cfg.dimensions.chest)
  }

  setWaist(value: number) {
    this.waist = this.fixUnit(value, this.cfg.dimensions.waist)
  }

  setHips(value: number) {
    this.hips = this.fixUnit(value, this.cfg.dimensions.hips)
  }

  async buildBaseRequest(type: Photo): Promise<BaseIdentityRequest> {
    const { pic_change } = this.common
    const image_desc = pic_change ? null : undefined

    if (type === 'simple') {
      const selfies = await this.waitSelfies()
      if (!selfies.length) throw new Error('no selfies')

      const userPhoto = await this.waitUserPhoto()
      if (!userPhoto) throw new Error('no userPhoto')

      return {
        identity_mode: 3,
        gender: this.gender,
        face1_url: selfies[0],
        body1_url: userPhoto,
        pic_change,
        image_desc,
      }
    }

    if (type === 'full') {
      const defaultExtendedShape: ExtendedShapeJson = {
        shape_type: ShapeType.HOURGLASS,
        height: '160-165',
        legLength: 50,
        body_thickness: BodyThickness.SLIM,
      }

      const dimensions = this.buildDimensions()

      const userPhoto = await this.waitUserPhoto()
      if (!userPhoto) throw new Error('no userPhoto')

      return {
        body: 2,
        breasts: 2,
        face1_url: '',
        face2_url: '',
        user_url: userPhoto,
        gender: this.gender,
        identity_mode: 2,
        skin_index: 1,
        extended_shape: JSON.stringify(defaultExtendedShape),
        dimensions: JSON.stringify(dimensions),
        pic_change,
        image_desc,
      }
    }

    const extended_shape: ExtendedShapeJson = {
      shape_type: this.shape,
      height: this.heightRange,
      legLength: opt.tsum ? this.buildTsumLegExtended() : this.legExtended,
      body_thickness: this.thickness,
    }

    const dimensions = this.buildDimensions()

    const selfies = await this.waitSelfies()
    if (!selfies.length) throw new Error('no selfies')

    return {
      body: this.body,
      breasts: this.bust,
      face1_url: selfies[0],
      face2_url: selfies[1],
      user_url: '',
      gender: this.gender,
      identity_mode: this.mode,
      skin_index: this.skin,
      extended_shape: JSON.stringify(extended_shape),
      dimensions: JSON.stringify(dimensions),
      pic_change,
      image_desc,
    }
  }

  buildTempRequest(type: Photo): Partial<BaseIdentityRequest> {
    const selfies = this.common.selfies.map(selfie => selfie.url)
    const userPhoto = this.common.userPhoto.url

    if (type === 'simple') {
      return {
        identity_mode: 3,
        gender: this.gender,
        face1_url: selfies[0],
        body1_url: userPhoto,
      }
    }

    if (type === 'full') {
      const dimensions = this.buildDimensions()
      const defaultExtendedShape: ExtendedShapeJson = {
        shape_type: ShapeType.HOURGLASS,
        height: '160-165',
        legLength: 50,
        body_thickness: BodyThickness.SLIM,
      }
      return {
        body: 2,
        breasts: 2,
        face1_url: undefined,
        face2_url: undefined,
        user_url: userPhoto,
        gender: this.gender,
        identity_mode: 2,
        skin_index: 1,
        extended_shape: JSON.stringify(defaultExtendedShape),
        dimensions: JSON.stringify(dimensions),
      }
    }

    const dimensions = this.buildDimensions()
    const extended_shape: ExtendedShapeJson = {
      shape_type: this.shape,
      height: this.heightRange,
      legLength: opt.tsum ? this.buildTsumLegExtended() : this.legExtended,
      body_thickness: this.thickness,
    }
    return {
      body: this.body,
      breasts: this.bust,
      face1_url: selfies[0],
      face2_url: selfies[1],
      user_url: undefined,
      gender: this.gender,
      identity_mode: this.mode,
      skin_index: this.skin,
      extended_shape: JSON.stringify(extended_shape),
      dimensions: JSON.stringify(dimensions),
    }
  }

  private buildDimensions(): DimensionsJson {
    return {
      height: this.height,
      shoulders: this.shoulders,
      chest: this.chest,
      waist: this.waist,
      hips: this.hips,
      legLength: opt.tsum ? this.buildTsumLegDetailed() : this.legDetailed,
    }
  }

  private async waitUserPhoto() {
    return await this.common.userPhoto.getUrlPromise()
  }

  private async waitSelfies() {
    const promises = this.common.selfies.map(selfie => selfie.getUrlPromise())
    const urls = await Promise.all(promises)
    return urls.filter(url => !!url)
  }

  private buildTsumLegExtended() {
    const height = getHeightFromRange(this.heightRange)
    return this.buildTsumLeg(height)
  }

  private buildTsumLegDetailed() {
    return this.buildTsumLeg(this.height)
  }

  private buildTsumLeg(height: number) {
    if (this.gender === 'male') {
      if (height <= 170) return 30
      if (height <= 179) return 32
      if (height <= 190) return 34
      return 36
    } else {
      if (height <= 164) return 28
      if (height <= 171) return 30
      if (height <= 178) return 32
      return 34
    }
  }

  private init(it: Identity | undefined, photo: Photo) {
    const g = this.gender
    const m = me.metrics
    const male = g === 'male'
    const cfg = this.cfg
    const d = it?.dimensions
    const ext = it?.extended_shape
    const heightRange = ext?.height ?? selectMyHeightRange(cfg, g) ?? selectHeightRange(cfg)
    const height = getHeightFromRange(heightRange)
    const legs = cfg.heights_config.find(c => c.height === heightRange)?.legLength ?? []
    const leg = ext?.legLength ?? m.leg ?? selectLeg(legs)

    this.skin = it?.skin_index ?? m.skin_index ?? 1
    this.mode = fixMode(it?.identity_mode, photo) ?? getDefaultIdentityMode(photo) ?? 0
    this.body = it?.body_index ?? m.body_index ?? 2
    this.bust = it?.breast_index ?? m.bust_index ?? 2
    this.shape = ext?.shape_type ?? m.shape_type ?? selectShapeType(cfg)
    this.thickness = ext?.body_thickness ?? m.body_thickness ?? selectBodyThickness(this.shape, cfg)
    this.heightRange = heightRange
    this.legExtended = leg
    this.height = d?.height ?? m.height ?? height
    this.shoulders = d?.shoulders ?? m.shoulders ?? getShouldersConfig(g).def
    this.chest = d?.chest ?? m.chest ?? (male ? 98 : 85)
    this.waist = d?.waist ?? m.waist ?? (male ? 86 : 65)
    this.hips = d?.hips ?? m.hips ?? (male ? 102 : 95)
    this.legDetailed = leg
  }

  private apply() {
    const data = this.data
    this.skin = data.skin
    this.mode = data.mode
    this.body = data.body
    this.bust = data.bust
    this.shape = data.shape
    this.thickness = data.thickness
    this.heightRange = data.heightRange
    this.legExtended = data.legExtended
    this.height = data.height
    this.shoulders = data.shoulders
    this.chest = data.chest
    this.waist = data.waist
    this.hips = data.hips
    this.legDetailed = data.legDetailed
  }

  private fix() {
    const cfg = this.cfg
    const { height, chest, waist, hips } = cfg.dimensions
    if (!cfg.shape_types.includes(this.shape)) {
      this.shape = selectShapeType(cfg)
    }
    const thicknesses = cfg.shape_types_config[this.shape] ?? cfg.body_thickness
    if (!thicknesses.includes(this.thickness)) {
      this.thickness = selectBodyThickness(this.shape, cfg)
    }
    if (!cfg.heights_config.some(c => c.height === this.heightRange)) {
      this.heightRange = selectHeightRange(cfg)
    }
    this.fixLegExtended()
    this.height = this.fixUnit(this.height, height)
    this.shoulders = this.fixShoulders(this.shoulders)
    this.chest = this.fixUnit(this.chest, chest)
    this.waist = this.fixUnit(this.waist, waist)
    this.hips = this.fixUnit(this.hips, hips)
    this.fixLegDetailed()
  }

  private fixUnit(value: number, cfg: MinMax): number {
    const inch = this.common.unit === 'in'
    return fixMinMax(value, cfg, inch)
  }

  private fixLegExtended() {
    const legs = this.cfg.heights_config.find(c => c.height === this.heightRange)?.legLength ?? []
    if (!legs.includes(this.legExtended)) {
      this.legExtended = selectLeg(legs)
    }
  }

  private fixLegDetailed() {
    this.legDetailed = fixMinMax(this.legDetailed, this.cfg.dimensions.legLength)
  }

  private fixShoulders(value: number) {
    return Math.max(20, Math.min(90, value))
  }

  private save() {
    this.data = {
      skin: this.skin,
      mode: this.mode,
      body: this.body,
      bust: this.bust,
      shape: this.shape,
      thickness: this.thickness,
      heightRange: this.heightRange,
      legExtended: this.legExtended,
      height: this.height,
      shoulders: this.shoulders,
      chest: this.chest,
      waist: this.waist,
      hips: this.hips,
      legDetailed: this.legDetailed,
    }
  }
}
